// src/pages/DataRoom.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://vsoomqfayfmtbnulnugn.supabase.co';

const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger:  '#C0222A', amber:   '#92640A',
  teal:    '#0D6E8A',
};

const CATEGORY_LABELS: Record<string, string> = {
  k1: 'K-1', capital_call: 'Capital Call', lp_agreement: 'LP Agreement',
  audit: 'Audit', factsheet: 'Factsheet', presentation: 'Presentation',
  tax: 'Tax Document', other: 'Other',
};

interface DataRoomDoc {
  id: string; firm_id: string; fund_id: string | null; folder_id: string | null;
  uploaded_by: string; file_name: string; file_size_bytes: number;
  mime_type: string | null; category: string; description: string | null;
  uploaded_at: string; fund_name?: string; firm_name?: string;
  access_count?: number; granted_to_count?: number;
}
interface Folder {
  id: string; firm_id: string; fund_id: string | null;
  parent_folder_id: string | null; name: string; created_at: string;
}
interface Connection {
  id: string; allocator_firm_id: string; manager_firm_id: string;
  allocator_firm: { id: string; name: string; type: string };
}
interface AccessGrant {
  id: string; document_id: string; allocator_firm_id: string;
  granted_at: string; revoked_at: string | null;
}
interface AccessLogEntry {
  id: string; document_id: string; accessed_by: string; accessed_by_firm: string;
  action: string; accessed_at: string;
  profile?: { full_name: string }; firm?: { name: string };
}

// ── Shared style primitives ───────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden',
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none', fontFamily: 'system-ui',
  marginBottom: '12px', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted,
  letterSpacing: '0.6px', textTransform: 'uppercase',
  display: 'block', marginBottom: '5px', fontFamily: 'system-ui',
};
const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none',
  padding: '8px 16px', borderRadius: '3px', fontSize: '12px',
  fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
const btnSecondary: React.CSSProperties = {
  background: C.white, color: C.textSec,
  border: `1px solid ${C.border}`,
  padding: '8px 14px', borderRadius: '3px', fontSize: '12px',
  cursor: 'pointer', fontFamily: 'system-ui',
};
const btnAction: React.CSSProperties = {
  background: C.white, color: C.teal,
  border: `1px solid rgba(13,110,138,0.3)`,
  borderRadius: '3px', padding: '4px 10px',
  fontSize: '11px', cursor: 'pointer', marginLeft: '4px', fontFamily: 'system-ui',
};
const btnDanger: React.CSSProperties = {
  background: C.white, color: C.danger,
  border: `1px solid rgba(192,34,42,0.3)`,
  borderRadius: '3px', padding: '4px 10px',
  fontSize: '11px', cursor: 'pointer', marginLeft: '4px', fontFamily: 'system-ui',
};

export default function DataRoom() {
  const { profile, firm, loading: profileLoading } = useProfile();
  const isManager = profile?.role === 'manager';

  const [docs, setDocs]               = useState<DataRoomDoc[]>([]);
  const [folders, setFolders]         = useState<Folder[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [funds, setFunds]             = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading]         = useState(true);

  const [activeContext, setActiveContext]     = useState<string>('firm');
  const [activeManagerId, setActiveManagerId] = useState<string>('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  const [showUploadModal, setShowUploadModal]       = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showAccessModal, setShowAccessModal]       = useState(false);
  const [showLogModal, setShowLogModal]             = useState(false);
  const [showMoveModal, setShowMoveModal]           = useState(false);

  const [selectedDoc, setSelectedDoc]           = useState<DataRoomDoc | null>(null);
  const [accessGrants, setAccessGrants]         = useState<AccessGrant[]>([]);
  const [accessLog, setAccessLog]               = useState<AccessLogEntry[]>([]);
  const [uploadFile, setUploadFile]             = useState<File | null>(null);
  const [uploadCategory, setUploadCategory]     = useState<string>('factsheet');
  const [uploadDescription, setUploadDescription] = useState<string>('');
  const [uploading, setUploading]               = useState(false);
  const [newFolderName, setNewFolderName]       = useState('');
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string | null>(null);

  useEffect(() => { if (firm) loadAll(); }, [firm?.id, isManager]);

  const loadAll = async () => {
    if (!firm) return;
    setLoading(true);
    if (isManager) {
      const [fundsRes, docsRes, foldersRes, connsRes] = await Promise.all([
        supabase.from('funds').select('id, name').eq('firm_id', firm.id).order('name'),
        supabase.from('data_room_documents').select('*, funds(name)').eq('firm_id', firm.id).is('deleted_at', null).order('uploaded_at', { ascending: false }),
        supabase.from('data_room_folders').select('*').eq('firm_id', firm.id).order('name'),
        supabase.from('connections').select('id, allocator_firm_id, manager_firm_id, allocator_firm:firms!connections_allocator_firm_id_fkey(id, name, type)').eq('manager_firm_id', firm.id).eq('status', 'approved'),
      ]);
      setFunds(fundsRes.data || []);
      setFolders(foldersRes.data || []);
      const docsList = (docsRes.data || []).map((d: any) => ({ ...d, fund_name: d.funds?.name }));
      const docIds = docsList.map((d) => d.id);
      if (docIds.length > 0) {
        const [logsRes, grantsRes] = await Promise.all([
          supabase.from('data_room_access_log').select('document_id').in('document_id', docIds),
          supabase.from('data_room_document_access').select('document_id').in('document_id', docIds).is('revoked_at', null),
        ]);
        const accessMap = new Map<string, number>();
        (logsRes.data || []).forEach((l: any) => accessMap.set(l.document_id, (accessMap.get(l.document_id) || 0) + 1));
        const grantMap = new Map<string, number>();
        (grantsRes.data || []).forEach((g: any) => grantMap.set(g.document_id, (grantMap.get(g.document_id) || 0) + 1));
        docsList.forEach((d) => { d.access_count = accessMap.get(d.id) || 0; d.granted_to_count = grantMap.get(d.id) || 0; });
      }
      setDocs(docsList);
      setConnections((connsRes.data || []) as any);
    } else {
      const [docsRes, foldersRes] = await Promise.all([
        supabase.from('data_room_documents').select('*, funds(name), firms(name)').is('deleted_at', null).order('uploaded_at', { ascending: false }),
        supabase.from('data_room_folders').select('*').order('name'),
      ]);
      setDocs((docsRes.data || []).map((d: any) => ({ ...d, fund_name: d.funds?.name, firm_name: d.firms?.name })));
      setFolders(foldersRes.data || []);
    }
    setLoading(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const allocatorManagers = useMemo(() => {
    if (isManager) return [];
    const map = new Map<string, { id: string; name: string; docs: DataRoomDoc[] }>();
    docs.forEach((d) => {
      if (!map.has(d.firm_id)) map.set(d.firm_id, { id: d.firm_id, name: d.firm_name || 'Unknown', docs: [] });
      map.get(d.firm_id)!.docs.push(d);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [docs, isManager]);

  useEffect(() => {
    if (!isManager && allocatorManagers.length > 0 && !activeManagerId)
      setActiveManagerId(allocatorManagers[0].id);
  }, [allocatorManagers, activeManagerId, isManager]);

  const effectiveFirmId = isManager ? firm?.id : activeManagerId;
  const effectiveFundId = activeContext === 'firm' ? null : activeContext;

  const contextFolders = useMemo(() => folders.filter((f) => f.firm_id === effectiveFirmId && (effectiveFundId ? f.fund_id === effectiveFundId : f.fund_id === null)), [folders, effectiveFirmId, effectiveFundId]);
  const contextDocs    = useMemo(() => docs.filter((d) => d.firm_id === effectiveFirmId && (effectiveFundId ? d.fund_id === effectiveFundId : d.fund_id === null)), [docs, effectiveFirmId, effectiveFundId]);
  const currentSubfolders = useMemo(() => contextFolders.filter((f) => f.parent_folder_id === currentFolderId), [contextFolders, currentFolderId]);
  const currentDocs       = useMemo(() => contextDocs.filter((d) => d.folder_id === currentFolderId), [contextDocs, currentFolderId]);

  const allocatorCurrentManagerFundIds = useMemo(() => {
    if (isManager || !activeManagerId) return [];
    const ids = new Set<string>();
    docs.forEach((d) => { if (d.firm_id === activeManagerId && d.fund_id) ids.add(d.fund_id); });
    return Array.from(ids);
  }, [docs, isManager, activeManagerId]);

  const breadcrumbs = useMemo(() => {
    const trail: Folder[] = [];
    let cursor = currentFolderId;
    while (cursor) {
      const f = folders.find((x) => x.id === cursor);
      if (!f) break;
      trail.unshift(f);
      cursor = f.parent_folder_id;
    }
    return trail;
  }, [currentFolderId, folders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !firm || !profile) return;
    const { error } = await supabase.from('data_room_folders').insert({ firm_id: firm.id, fund_id: effectiveFundId, parent_folder_id: currentFolderId, name: newFolderName.trim(), created_by: profile.id });
    if (error) { alert(`Folder create failed: ${error.message}`); return; }
    setNewFolderName(''); setShowNewFolderModal(false); await loadAll();
  };

  const handleDeleteFolder = async (folder: Folder) => {
    const descendantIds = new Set<string>(); const queue = [folder.id];
    while (queue.length) { const cur = queue.shift()!; descendantIds.add(cur); folders.filter((f) => f.parent_folder_id === cur).forEach((f) => queue.push(f.id)); }
    const docsInside = docs.filter((d) => d.folder_id && descendantIds.has(d.folder_id)).length;
    const subfoldersInside = descendantIds.size - 1;
    if (docsInside > 0 || subfoldersInside > 0) {
      const conf = window.prompt(`Delete "${folder.name}"?\n\nThis will permanently delete:\n• ${subfoldersInside} subfolder${subfoldersInside !== 1 ? 's' : ''}\n• ${docsInside} document${docsInside !== 1 ? 's' : ''}\n\nType DELETE to confirm.`);
      if (conf !== 'DELETE') return;
    } else { if (!window.confirm(`Delete empty folder "${folder.name}"?`)) return; }
    const { data: docsToDelete } = await supabase.from('data_room_documents').select('id').in('folder_id', Array.from(descendantIds)).is('deleted_at', null);
    if (docsToDelete?.length) {
      const { data: { session } } = await supabase.auth.getSession();
      await Promise.all(docsToDelete.map((d: any) => fetch(`${SUPABASE_URL}/functions/v1/dataroom-delete-document`, { method: 'POST', headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ document_id: d.id }) })));
    }
    const { error } = await supabase.from('data_room_folders').delete().eq('id', folder.id);
    if (error) { alert(`Folder delete failed: ${error.message}`); return; }
    await loadAll();
  };

  const handleRenameFolder = async (folder: Folder) => {
    const newName = window.prompt('Rename folder:', folder.name);
    if (!newName || newName.trim() === folder.name) return;
    const { error } = await supabase.from('data_room_folders').update({ name: newName.trim() }).eq('id', folder.id);
    if (error) { alert(`Rename failed: ${error.message}`); return; }
    await loadAll();
  };

  const handleUpload = async () => {
    if (!uploadFile || !firm || !profile) return;
    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const upRes = await fetch(`${SUPABASE_URL}/functions/v1/dataroom-upload-url`, { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ firm_id: firm.id, fund_id: effectiveFundId, file_name: uploadFile.name, mime_type: uploadFile.type || 'application/octet-stream', file_size_bytes: uploadFile.size, category: uploadCategory, description: uploadDescription || null }) });
      const upJson = await upRes.json();
      if (!upRes.ok) throw new Error(upJson.error || 'Upload URL generation failed');
      const putRes = await fetch(upJson.upload_url, { method: 'PUT', headers: { 'Content-Type': uploadFile.type || 'application/octet-stream' }, body: uploadFile });
      if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);
      logActivity(profile.id, firm.id, 'document_uploaded', 'document', upJson.document?.id || uploadFile.name, { file_name: uploadFile.name, category: uploadCategory });
      if (currentFolderId && upJson.document?.id) await supabase.from('data_room_documents').update({ folder_id: currentFolderId }).eq('id', upJson.document.id);
      setShowUploadModal(false); setUploadFile(null); setUploadCategory('factsheet'); setUploadDescription(''); await loadAll();
    } catch (err: any) { alert(`Upload failed: ${err.message}`); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc: DataRoomDoc, action: 'view' | 'download') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${SUPABASE_URL}/functions/v1/dataroom-download-url`, { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ document_id: doc.id, action }) });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Download failed');
      if (profile) logActivity(profile.id, firm?.id ?? null, 'document_accessed', 'document', doc.id, { file_name: doc.file_name, category: doc.category, access_action: action });
      if (action === 'view') { window.open(json.download_url, '_blank'); }
      else { const link = document.createElement('a'); link.href = json.download_url; link.download = doc.file_name; document.body.appendChild(link); link.click(); document.body.removeChild(link); }
    } catch (err: any) { alert(`Download failed: ${err.message}`); }
  };

  const handleDelete = async (doc: DataRoomDoc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/dataroom-delete-document`, { method: 'POST', headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ document_id: doc.id }) });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      await loadAll();
    } catch (err: any) { alert(`Delete failed: ${err.message}`); }
  };

  const handleMoveDoc = async () => {
    if (!selectedDoc) return;
    const { error } = await supabase.from('data_room_documents').update({ folder_id: moveTargetFolderId }).eq('id', selectedDoc.id);
    if (error) { alert(`Move failed: ${error.message}`); return; }
    setShowMoveModal(false); setSelectedDoc(null); setMoveTargetFolderId(null); await loadAll();
  };

  const openAccessModal = async (doc: DataRoomDoc) => {
    setSelectedDoc(doc); setShowAccessModal(true);
    const { data } = await supabase.from('data_room_document_access').select('*, allocator_firm:firms!data_room_document_access_allocator_firm_id_fkey(id, name)').eq('document_id', doc.id);
    setAccessGrants((data || []) as any);
  };

  const toggleGrant = async (allocatorFirmId: string) => {
    if (!selectedDoc || !profile) return;
    const existing = accessGrants.find((g) => g.allocator_firm_id === allocatorFirmId && !g.revoked_at);
    if (existing) {
      await supabase.from('data_room_document_access').update({ revoked_at: new Date().toISOString() }).eq('id', existing.id);
    } else {
      const revoked = accessGrants.find((g) => g.allocator_firm_id === allocatorFirmId && g.revoked_at);
      if (revoked) await supabase.from('data_room_document_access').update({ revoked_at: null, granted_at: new Date().toISOString() }).eq('id', revoked.id);
      else await supabase.from('data_room_document_access').insert({ document_id: selectedDoc.id, allocator_firm_id: allocatorFirmId, granted_by: profile.id });
    }
    const { data } = await supabase.from('data_room_document_access').select('*, allocator_firm:firms!data_room_document_access_allocator_firm_id_fkey(id, name)').eq('document_id', selectedDoc.id);
    setAccessGrants((data || []) as any); await loadAll();
  };

  const openLogModal = async (doc: DataRoomDoc) => {
    setSelectedDoc(doc); setShowLogModal(true);
    const { data } = await supabase.from('data_room_access_log').select('*, profile:profiles!data_room_access_log_accessed_by_fkey(full_name), firm:firms!data_room_access_log_accessed_by_firm_fkey(name)').eq('document_id', doc.id).order('accessed_at', { ascending: false });
    setAccessLog((data || []) as any);
  };

  const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '24px', width: '480px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 400, color: C.navy, marginBottom: '16px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>{title}</div>
        {children}
      </div>
    </div>
  );

  if (profileLoading || loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Data Room</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
          {isManager ? 'Upload and manage documents for your investors. Documents are private by default.' : 'Documents shared with you by your managers.'}
        </p>
      </div>

      {/* Allocator: manager tabs */}
      {!isManager && allocatorManagers.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '8px' }}>Manager</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {allocatorManagers.map((m) => (
              <button key={m.id} onClick={() => { setActiveManagerId(m.id); setActiveContext('firm'); setCurrentFolderId(null); }} style={{
                padding: '6px 14px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', border: 'none',
                background: activeManagerId === m.id ? C.navy : C.white,
                color: activeManagerId === m.id ? C.white : C.textSec,
                border2: activeManagerId === m.id ? 'none' : `1px solid ${C.border}`,
                fontFamily: 'system-ui', fontWeight: 500,
              } as any}>
                {m.name} <span style={{ opacity: 0.6, fontSize: '10px' }}>{m.docs.length}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Section tabs (firm / fund) */}
      {(isManager || (!isManager && activeManagerId)) && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '8px' }}>Section</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {[{ id: 'firm', name: 'Firm-level' },
              ...(isManager ? funds : allocatorCurrentManagerFundIds.map(fid => ({ id: fid, name: docs.find(d => d.fund_id === fid)?.fund_name || 'Fund' })))
            ].map(item => (
              <button key={item.id} onClick={() => { setActiveContext(item.id); setCurrentFolderId(null); }} style={{
                padding: '6px 14px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
                background: activeContext === item.id ? C.navy : C.white,
                color: activeContext === item.id ? C.white : C.textSec,
                border: activeContext === item.id ? 'none' : `1px solid ${C.border}`,
                fontFamily: 'system-ui', fontWeight: 500,
              }}>{item.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      {(isManager || activeManagerId) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: C.muted, marginBottom: '14px', flexWrap: 'wrap' }}>
          <span style={{ color: C.navy, cursor: 'pointer', fontWeight: 600 }} onClick={() => setCurrentFolderId(null)}>
            {activeContext === 'firm' ? 'Firm-level' : (funds.find(f => f.id === activeContext)?.name || docs.find(d => d.fund_id === activeContext)?.fund_name || 'Fund')}
          </span>
          {breadcrumbs.map((f, i) => (
            <React.Fragment key={f.id}>
              <span style={{ color: C.borderLt }}>/</span>
              {i === breadcrumbs.length - 1
                ? <span style={{ color: C.text, fontWeight: 600 }}>{f.name}</span>
                : <span style={{ color: C.teal, cursor: 'pointer' }} onClick={() => setCurrentFolderId(f.id)}>{f.name}</span>}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Manager toolbar */}
      {isManager && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <button style={btnPrimary} onClick={() => setShowUploadModal(true)}>+ Upload Document</button>
          <button style={btnSecondary} onClick={() => setShowNewFolderModal(true)}>+ New Folder</button>
        </div>
      )}

      {/* Content */}
      <div style={card}>
        {currentSubfolders.length === 0 && currentDocs.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>
            {isManager
              ? (currentFolderId ? 'This folder is empty.' : 'Nothing here yet. Upload a document or create a folder.')
              : (allocatorManagers.length === 0 ? 'No documents have been shared with you yet.' : 'Nothing in this section.')}
          </div>
        ) : (
          <>
            {currentSubfolders.map((folder) => {
              const descendantIds = new Set<string>([folder.id]);
              const queue = [folder.id];
              while (queue.length) { const cur = queue.shift()!; contextFolders.filter(f => f.parent_folder_id === cur).forEach(f => { descendantIds.add(f.id); queue.push(f.id); }); }
              const docCount = contextDocs.filter(d => d.folder_id && descendantIds.has(d.folder_id)).length;
              return (
                <div key={folder.id} onClick={() => setCurrentFolderId(folder.id)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: `1px solid ${C.borderLt}`, cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: '#EFF8FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>📁</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{folder.name}</div>
                    <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>{docCount} document{docCount !== 1 ? 's' : ''}</div>
                  </div>
                  {isManager && (
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                      <button style={btnAction} onClick={() => handleRenameFolder(folder)}>Rename</button>
                      <button style={btnDanger} onClick={() => handleDeleteFolder(folder)}>Delete</button>
                    </div>
                  )}
                </div>
              );
            })}

            {currentDocs.map((doc) => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: `1px solid ${C.borderLt}` }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{doc.file_name}</div>
                  <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ background: '#EFF8FB', color: C.teal, border: `1px solid rgba(13,110,138,0.2)`, padding: '1px 6px', borderRadius: '2px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{CATEGORY_LABELS[doc.category] || doc.category}</span>
                    <span>{formatSize(doc.file_size_bytes)}</span>
                    <span>·</span>
                    <span>{formatDate(doc.uploaded_at)}</span>
                    {doc.description && <><span>·</span><span>{doc.description}</span></>}
                  </div>
                  {isManager && (
                    <div style={{ fontSize: '11px', color: C.muted, marginTop: '3px' }}>
                      {doc.granted_to_count === 0
                        ? <span style={{ color: C.amber, fontWeight: 600 }}>Private, not shared with any allocators</span>
                        : <span style={{ color: C.success, fontWeight: 600 }}>Shared with {doc.granted_to_count} allocator{doc.granted_to_count !== 1 ? 's' : ''}</span>}
                      {(doc.access_count ?? 0) > 0 && <span style={{ marginLeft: '8px', color: C.mutedLt }}>{doc.access_count} view{doc.access_count !== 1 ? 's' : ''}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <button style={btnAction} onClick={() => handleDownload(doc, 'view')}>View</button>
                  <button style={btnAction} onClick={() => handleDownload(doc, 'download')}>Download</button>
                  {isManager && <>
                    <button style={btnAction} onClick={() => openAccessModal(doc)}>Access</button>
                    <button style={btnAction} onClick={() => openLogModal(doc)}>Log</button>
                    <button style={btnAction} onClick={() => { setSelectedDoc(doc); setMoveTargetFolderId(doc.folder_id); setShowMoveModal(true); }}>Move</button>
                    <button style={btnDanger} onClick={() => handleDelete(doc)}>Delete</button>
                  </>}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Upload modal */}
      {showUploadModal && (
        <Modal title="Upload Document" onClose={() => !uploading && setShowUploadModal(false)}>
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px' }}>
            Destination: <strong>{breadcrumbs.length > 0 ? breadcrumbs.map(b => b.name).join(' / ') : 'Root'}</strong>
          </div>
          <label style={labelStyle}>File</label>
          <input type="file" onChange={e => setUploadFile(e.target.files?.[0] || null)} style={inputStyle} />
          <label style={labelStyle}>Category</label>
          <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} style={inputStyle}>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label style={labelStyle}>Description (optional)</label>
          <input type="text" value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} placeholder="e.g. Q1 2026 capital call notice" style={inputStyle} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button style={{ ...btnPrimary, opacity: uploading || !uploadFile ? 0.5 : 1 }} disabled={uploading || !uploadFile} onClick={handleUpload}>{uploading ? 'Uploading...' : 'Upload'}</button>
            <button style={btnSecondary} onClick={() => setShowUploadModal(false)} disabled={uploading}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* New folder modal */}
      {showNewFolderModal && (
        <Modal title="New Folder" onClose={() => setShowNewFolderModal(false)}>
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px' }}>
            Inside: <strong>{breadcrumbs.length > 0 ? breadcrumbs.map(b => b.name).join(' / ') : 'Root'}</strong>
          </div>
          <label style={labelStyle}>Folder name</label>
          <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="e.g. Q1 2026" style={inputStyle} autoFocus onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...btnPrimary, opacity: !newFolderName.trim() ? 0.5 : 1 }} onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</button>
            <button style={btnSecondary} onClick={() => setShowNewFolderModal(false)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Move modal */}
      {showMoveModal && selectedDoc && (
        <Modal title="Move Document" onClose={() => setShowMoveModal(false)}>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px' }}>{selectedDoc.file_name}</div>
          <label style={labelStyle}>Destination folder</label>
          <select value={moveTargetFolderId || ''} onChange={e => setMoveTargetFolderId(e.target.value || null)} style={inputStyle}>
            <option value="">Root (no folder)</option>
            {contextFolders.map(f => {
              const path: string[] = []; let cur: Folder | undefined = f;
              while (cur) { path.unshift(cur.name); cur = contextFolders.find(x => x.id === cur!.parent_folder_id); }
              return <option key={f.id} value={f.id}>{path.join(' / ')}</option>;
            })}
          </select>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={btnPrimary} onClick={handleMoveDoc}>Move</button>
            <button style={btnSecondary} onClick={() => setShowMoveModal(false)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Access modal */}
      {showAccessModal && selectedDoc && (
        <Modal title="Manage Access" onClose={() => setShowAccessModal(false)}>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px' }}>{selectedDoc.file_name}</div>
          {connections.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>No approved investor connections yet.</div>
          ) : connections.map(c => {
            const granted = accessGrants.some(g => g.allocator_firm_id === c.allocator_firm_id && !g.revoked_at);
            return (
              <div key={c.id} onClick={() => toggleGrant(c.allocator_firm_id)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '3px', cursor: 'pointer', marginBottom: '4px', background: granted ? '#F0FDF4' : C.surface, border: `1px solid ${granted ? '#BBF7D0' : C.border}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{c.allocator_firm.name}</div>
                  <div style={{ fontSize: '11px', color: C.muted }}>{c.allocator_firm.type}</div>
                </div>
                <div style={{ width: '20px', height: '20px', borderRadius: '3px', border: `2px solid ${granted ? C.success : C.border}`, background: granted ? C.success : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: '12px', fontWeight: 700 }}>
                  {granted ? '✓' : ''}
                </div>
              </div>
            );
          })}
          <button style={{ ...btnSecondary, marginTop: '14px', width: '100%' }} onClick={() => setShowAccessModal(false)}>Done</button>
        </Modal>
      )}

      {/* Log modal */}
      {showLogModal && selectedDoc && (
        <Modal title="Access Log" onClose={() => setShowLogModal(false)}>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px' }}>{selectedDoc.file_name}</div>
          {accessLog.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>No views or downloads yet.</div>
          ) : accessLog.map(entry => (
            <div key={entry.id} style={{ padding: '8px 0', borderBottom: `1px solid ${C.borderLt}` }}>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: C.text }}>{entry.profile?.full_name || 'Unknown'}</div>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                {entry.firm?.name} · {entry.action === 'download' ? 'Downloaded' : 'Viewed'} · {new Date(entry.accessed_at).toLocaleString('en-GB')}
              </div>
            </div>
          ))}
          <button style={{ ...btnSecondary, marginTop: '14px', width: '100%' }} onClick={() => setShowLogModal(false)}>Close</button>
        </Modal>
      )}
    </div>
  );
}